import { defineMessages } from '../define';

/**
 * Webhook endpoints and their delivery log.
 *
 * Event names (`message.new`, `session.qr`, …) are wire identifiers and never
 * appear here — only the one-line explanation beside each one does.
 */
export const webhooks = defineMessages({
  en: {
    'webhooks.eventHelp.bank.transaction':
      'A bank alert read into a transaction. Only those naming the other side are sent, and again if a later read changes one.',
    'webhooks.filterEvent': 'Filter by event',
    'webhooks.event.all': 'All events',
    'webhooks.clear.button': 'Clear history',
    'webhooks.clear.buttonEvent': 'Clear these',
    'webhooks.clear.titleAll': 'Clear the delivery history?',
    'webhooks.clear.titleEvent': 'Clear every {event} delivery?',
    'webhooks.clear.description':
      'Settled attempts are removed for good. Anything still queued is kept, and the endpoint itself is untouched.',
    'webhooks.clear.action': 'Clear',
    'webhooks.clear.done': 'Removed {count} delivery records',
    'webhooks.title': 'Webhooks',
    'webhooks.description':
      'Threadline POSTs a signed JSON envelope to these URLs as events happen, and retries with backoff until the endpoint answers 2xx.',
    'webhooks.add': 'Add endpoint',
    'webhooks.create': 'Create endpoint',

    'webhooks.empty.title': 'No webhook endpoints',
    'webhooks.empty.description':
      'A webhook is a URL of yours that Threadline calls with a signed JSON body whenever something happens, so your systems learn about a message without polling for it.',

    'webhooks.label.status': 'Status',
    'webhooks.label.name': 'Name',
    'webhooks.label.url': 'URL',
    'webhooks.label.events': 'Events',
    'webhooks.label.event': 'Event',
    'webhooks.label.sessions': 'Sessions',
    'webhooks.label.last24h': '24h',
    'webhooks.label.lastDelivery': 'Last delivery',
    'webhooks.label.attempt': 'Attempt',
    'webhooks.label.response': 'Response',
    'webhooks.label.latency': 'Latency',
    'webhooks.label.created': 'Created',
    'webhooks.label.payload': 'Payload',
    'webhooks.label.error': 'Error',
    'webhooks.label.result': 'Result',
    'webhooks.label.maxRetries': 'Max retries',
    'webhooks.label.timeout': 'Timeout (ms)',
    'webhooks.label.customHeaders': 'Custom headers',

    'webhooks.pause.name': 'Pause {name}',
    'webhooks.activate.name': 'Activate {name}',
    'webhooks.actionsFor': 'Actions for {name}',
    'webhooks.copyUrl': 'Copy URL',
    'webhooks.allEvents': 'All events',
    'webhooks.sendTest': 'Send test',
    'webhooks.viewDeliveries': 'View deliveries',

    'webhooks.toast.updated': 'Endpoint updated',
    'webhooks.toast.deleted': 'Endpoint deleted',
    'webhooks.toast.testQueued': 'Test queued',
    'webhooks.toast.testQueued.detail': 'Watch the deliveries tab for the result.',
    'webhooks.toast.redeliverQueued': 'Queued for redelivery',
    'webhooks.requestFailed':
      'The request could not be completed. Check that the gateway is reachable.',

    'webhooks.delete.title': 'Delete {name}?',
    'webhooks.delete.description':
      'Its delivery history goes with it. Events already queued will not be sent.',
    'webhooks.delete.descriptionShort': 'Its delivery history goes with it.',
    'webhooks.delete.confirm': 'Delete endpoint',

    'webhooks.form.editTitle': 'Edit endpoint',
    'webhooks.form.description':
      "Threadline signs every request with this endpoint's secret so your receiver can prove the call came from here.",
    'webhooks.name.placeholder': 'Ops relay',
    'webhooks.url.hint':
      'Must be http or https. A private address such as http://n8n:5678 is fine on a self-hosted install.',
    'webhooks.url.invalid': 'That is not a URL. Include the scheme, for example https://example.com/hook.',
    'webhooks.url.scheme': 'Only http and https endpoints can be called.',
    'webhooks.events.noneWarning': 'No events selected — this endpoint will receive everything.',
    'webhooks.sessions.all': 'All sessions, including ones added later',
    'webhooks.sessions.empty': 'No sessions yet.',
    'webhooks.headers.add': 'Add header',
    'webhooks.headers.hint':
      'Sent on every request. The signature headers always win over anything set here.',
    'webhooks.header.nameLabel': 'Header {index} name',
    'webhooks.header.valueLabel': 'Header {index} value',
    'webhooks.header.remove': 'Remove header {index}',
    'webhooks.maxRetries.hint': '0 to 10. Backoff runs 1s to 52m.',
    'webhooks.timeout.hint': '1000 to 60000.',

    'webhooks.eventHelp.message.new': 'A message arrives in a watched chat, inbound or outbound.',
    'webhooks.eventHelp.message.updated': 'A stored message changes — an edit, or a read receipt.',
    'webhooks.eventHelp.message.deleted': 'A message is recalled or removed from the chat.',
    'webhooks.eventHelp.session.connected':
      'A session finishes signing in and starts receiving.',
    'webhooks.eventHelp.session.disconnected': 'A session loses its LINE connection.',
    'webhooks.eventHelp.session.qr': 'A new QR code is issued and is waiting to be scanned.',
    'webhooks.eventHelp.keyword.hit': 'An incoming message matches one of your keyword rules.',
    'webhooks.eventHelp.chat.updated':
      'A chat is renamed, pinned, muted, or its members change.',

    'webhooks.secret.title': 'Signing secret',
    'webhooks.secret.description': '{name} was created. Copy the secret now.',
    'webhooks.secret.fallbackName': 'The endpoint',
    'webhooks.secret.warning':
      'This is the only time the secret is shown. It is encrypted at rest and cannot be read back — if you lose it you will have to recreate the endpoint.',
    'webhooks.secret.copy': 'Copy signing secret',
    'webhooks.secret.verifyToggle': 'How to verify the signature',
    // Four fragments because the sentence wraps three literal code spans; each
    // fragment is a clause a translator can reorder inside.
    'webhooks.secret.verify.a': 'Every request carries the header',
    'webhooks.secret.verify.b': 'Compute an HMAC-SHA256 of',
    'webhooks.secret.verify.c': 'with the secret and compare it against',
    'webhooks.secret.verify.d': 'in constant time.',
    'webhooks.secret.acknowledge': 'I have copied it',

    'webhooks.notFound.title': 'Endpoint not found',
    'webhooks.notFound.description': 'It may have been deleted since this link was shared.',
    'webhooks.backToList': 'Back to webhooks',
    'webhooks.paused': 'Paused',

    'webhooks.metric.delivered24h': 'Delivered 24h',
    'webhooks.metric.failed24h': 'Failed 24h',
    'webhooks.metric.dead': 'Dead letters',
    'webhooks.metric.dead.hint': 'Attempts exhausted',
    'webhooks.metric.p95': 'p95 latency',
    'webhooks.metric.p95.hint': 'Across the loaded page',

    'webhooks.tab.deliveries': 'Deliveries',
    'webhooks.tab.tester': 'Tester',

    'webhooks.deliveryStatus.PENDING': 'Queued',
    'webhooks.deliveryStatus.DELIVERING': 'Sending',
    'webhooks.deliveryStatus.SUCCEEDED': 'Delivered',
    'webhooks.deliveryStatus.FAILED': 'Failed, will retry',
    'webhooks.deliveryStatus.DEAD': 'Given up',

    'webhooks.filterStatus': 'Filter by status',
    'webhooks.status.all': 'All statuses',
    'webhooks.liveNotice': 'Updates live as attempts settle',
    'webhooks.redeliver': 'Redeliver',
    'webhooks.deliveries.empty.title': 'No deliveries yet',
    'webhooks.deliveries.empty.description':
      'Attempts appear here as soon as this endpoint receives its first event. Send a test to check the connection.',

    'webhooks.delivery.title': 'Delivery',
    'webhooks.delivery.attemptOf': 'Attempt {attempt} of {max} · {latency}',
    'webhooks.section.envelope': 'Request envelope',
    'webhooks.section.retrySchedule': 'Retry schedule',
    'webhooks.envelope.dataNotice':
      'The data block is not returned by the deliveries endpoint; the fields above are the ones the receiver saw in the envelope header.',
    'webhooks.response.none': 'No response was received. See the error below.',
    'webhooks.redeliverEnvelope': 'Redeliver this envelope',

    'webhooks.retry.deliveredOn': 'Delivered on attempt {attempt}.',
    'webhooks.retry.stopped.one':
      'Stopped after {count} attempt. Redeliver to try again with the same envelope.',
    'webhooks.retry.stopped.other':
      'Stopped after {count} attempts. Redeliver to try again with the same envelope.',
    'webhooks.retry.try': 'try {attempt}',
    'webhooks.retry.immediately': 'immediately',
    'webhooks.retry.after': '+{delay} after the previous',
    'webhooks.retry.next': 'next',

    'webhooks.tester.eventLabel': 'Event to send',
    'webhooks.tester.ping': 'ping (connectivity check)',
    'webhooks.tester.payloadLabel': 'Test payload',
    'webhooks.tester.invalidJson': 'Not valid JSON: {error}',
    'webhooks.tester.send': 'Send',
    'webhooks.tester.idle':
      'Nothing sent yet. The response appears here once the endpoint answers or the attempt times out after {timeout}.',
    'webhooks.tester.headers': 'Headers sent',
    'webhooks.tester.headersNote':
      "The signature digest is computed on the server from this endpoint's secret; only its shape is shown here.",
  },
  th: {
    'webhooks.eventHelp.bank.transaction':
      'รายการเดินบัญชีที่อ่านได้จากข้อความธนาคาร ส่งเฉพาะรายการที่ระบุคู่โอน และส่งซ้ำเมื่อการอ่านรอบหลังทำให้ข้อมูลเปลี่ยน',
    'webhooks.filterEvent': 'กรองตามเหตุการณ์',
    'webhooks.event.all': 'ทุกเหตุการณ์',
    'webhooks.clear.button': 'ล้างประวัติ',
    'webhooks.clear.buttonEvent': 'ล้างเฉพาะที่กรองอยู่',
    'webhooks.clear.titleAll': 'ล้างประวัติการส่งทั้งหมดหรือไม่',
    'webhooks.clear.titleEvent': 'ล้างประวัติของ {event} ทั้งหมดหรือไม่',
    'webhooks.clear.description':
      'รายการที่ส่งเสร็จแล้วจะถูกลบถาวร ส่วนที่ยังรออยู่ในคิวจะไม่ถูกแตะ และตัว endpoint ยังอยู่เหมือนเดิม',
    'webhooks.clear.action': 'ล้าง',
    'webhooks.clear.done': 'ลบประวัติการส่งไป {count} รายการ',
    'webhooks.title': 'Webhooks',
    'webhooks.description':
      'Threadline จะ POST ซองข้อมูล JSON ที่ลงลายเซ็นไปยัง URL เหล่านี้ทันทีที่มีเหตุการณ์เกิดขึ้น และจะลองส่งซ้ำโดยเว้นระยะห่างขึ้นเรื่อย ๆ จนกว่า endpoint จะตอบ 2xx',
    'webhooks.add': 'เพิ่ม endpoint',
    'webhooks.create': 'สร้าง endpoint',

    'webhooks.empty.title': 'ยังไม่มี endpoint สำหรับ webhook',
    'webhooks.empty.description':
      'Webhook คือ URL ของคุณที่ Threadline จะเรียกพร้อมเนื้อหา JSON ที่ลงลายเซ็นทุกครั้งที่มีเหตุการณ์เกิดขึ้น ระบบของคุณจึงรับรู้ข้อความใหม่ได้โดยไม่ต้องคอยดึงข้อมูลเอง',

    'webhooks.label.status': 'สถานะ',
    'webhooks.label.name': 'ชื่อ',
    'webhooks.label.url': 'URL',
    'webhooks.label.events': 'เหตุการณ์',
    'webhooks.label.event': 'เหตุการณ์',
    'webhooks.label.sessions': 'เซสชัน',
    'webhooks.label.last24h': '24 ชม.',
    'webhooks.label.lastDelivery': 'ส่งล่าสุด',
    'webhooks.label.attempt': 'ครั้งที่',
    'webhooks.label.response': 'การตอบกลับ',
    'webhooks.label.latency': 'ความหน่วง',
    'webhooks.label.created': 'สร้างเมื่อ',
    'webhooks.label.payload': 'Payload',
    'webhooks.label.error': 'ข้อผิดพลาด',
    'webhooks.label.result': 'ผลลัพธ์',
    'webhooks.label.maxRetries': 'ลองซ้ำสูงสุด',
    'webhooks.label.timeout': 'หมดเวลา (มิลลิวินาที)',
    'webhooks.label.customHeaders': 'เฮดเดอร์ที่กำหนดเอง',

    'webhooks.pause.name': 'หยุด {name} ชั่วคราว',
    'webhooks.activate.name': 'เปิดใช้งาน {name}',
    'webhooks.actionsFor': 'การทำงานสำหรับ {name}',
    'webhooks.copyUrl': 'คัดลอก URL',
    'webhooks.allEvents': 'ทุกเหตุการณ์',
    'webhooks.sendTest': 'ส่งทดสอบ',
    'webhooks.viewDeliveries': 'ดูประวัติการส่ง',

    'webhooks.toast.updated': 'อัปเดต endpoint แล้ว',
    'webhooks.toast.deleted': 'ลบ endpoint แล้ว',
    'webhooks.toast.testQueued': 'เข้าคิวส่งทดสอบแล้ว',
    'webhooks.toast.testQueued.detail': 'ดูผลลัพธ์ได้ที่แท็บการส่ง',
    'webhooks.toast.redeliverQueued': 'เข้าคิวส่งซ้ำแล้ว',
    'webhooks.requestFailed': 'ดำเนินการตามคำขอไม่สำเร็จ กรุณาตรวจสอบว่าเชื่อมต่อเกตเวย์ได้',

    'webhooks.delete.title': 'ลบ {name} หรือไม่',
    'webhooks.delete.description':
      'ประวัติการส่งทั้งหมดจะถูกลบไปด้วย และเหตุการณ์ที่รออยู่ในคิวจะไม่ถูกส่ง',
    'webhooks.delete.descriptionShort': 'ประวัติการส่งทั้งหมดจะถูกลบไปด้วย',
    'webhooks.delete.confirm': 'ลบ endpoint',

    'webhooks.form.editTitle': 'แก้ไข endpoint',
    'webhooks.form.description':
      'Threadline จะลงลายเซ็นทุกคำขอด้วย secret ของ endpoint นี้ เพื่อให้ปลายทางตรวจสอบได้ว่าคำขอมาจากที่นี่จริง',
    'webhooks.name.placeholder': 'รีเลย์ทีมปฏิบัติการ',
    'webhooks.url.hint':
      'ต้องเป็น http หรือ https หากติดตั้งใช้งานเอง จะใช้ที่อยู่ภายในอย่าง http://n8n:5678 ก็ได้',
    'webhooks.url.invalid': 'นี่ไม่ใช่ URL ที่ถูกต้อง กรุณาใส่ scheme ด้วย เช่น https://example.com/hook',
    'webhooks.url.scheme': 'เรียกได้เฉพาะ endpoint ที่เป็น http และ https เท่านั้น',
    'webhooks.events.noneWarning': 'ยังไม่ได้เลือกเหตุการณ์ — endpoint นี้จะได้รับทุกเหตุการณ์',
    'webhooks.sessions.all': 'ทุกเซสชัน รวมถึงเซสชันที่เพิ่มในภายหลัง',
    'webhooks.sessions.empty': 'ยังไม่มีเซสชัน',
    'webhooks.headers.add': 'เพิ่มเฮดเดอร์',
    'webhooks.headers.hint':
      'ส่งไปพร้อมทุกคำขอ ทั้งนี้เฮดเดอร์ลายเซ็นจะมีผลเหนือค่าที่ตั้งไว้ตรงนี้เสมอ',
    'webhooks.header.nameLabel': 'ชื่อเฮดเดอร์ที่ {index}',
    'webhooks.header.valueLabel': 'ค่าเฮดเดอร์ที่ {index}',
    'webhooks.header.remove': 'ลบเฮดเดอร์ที่ {index}',
    'webhooks.maxRetries.hint': '0 ถึง 10 ระยะหน่วงอยู่ระหว่าง 1 วินาที ถึง 52 นาที',
    'webhooks.timeout.hint': '1000 ถึง 60000',

    'webhooks.eventHelp.message.new': 'มีข้อความเข้ามาในแชทที่เฝ้าดูอยู่ ทั้งขาเข้าและขาออก',
    'webhooks.eventHelp.message.updated':
      'ข้อความที่เก็บไว้มีการเปลี่ยนแปลง เช่น การแก้ไข หรือสถานะอ่านแล้ว',
    'webhooks.eventHelp.message.deleted': 'มีการยกเลิกส่งหรือลบข้อความออกจากแชท',
    'webhooks.eventHelp.session.connected': 'เซสชันเข้าสู่ระบบสำเร็จและเริ่มรับข้อความ',
    'webhooks.eventHelp.session.disconnected': 'เซสชันขาดการเชื่อมต่อกับ LINE',
    'webhooks.eventHelp.session.qr': 'มี QR code ใหม่ออกมาและกำลังรอการสแกน',
    'webhooks.eventHelp.keyword.hit': 'ข้อความขาเข้าตรงกับกฎคำสำคัญที่คุณตั้งไว้',
    'webhooks.eventHelp.chat.updated': 'แชทถูกเปลี่ยนชื่อ ปักหมุด ปิดเสียง หรือมีสมาชิกเปลี่ยนแปลง',

    'webhooks.secret.title': 'Secret สำหรับลงลายเซ็น',
    'webhooks.secret.description': 'สร้าง {name} เรียบร้อยแล้ว กรุณาคัดลอก secret ตอนนี้',
    'webhooks.secret.fallbackName': 'endpoint นี้',
    'webhooks.secret.warning':
      'นี่เป็นครั้งเดียวที่ระบบจะแสดง secret นี้ ระบบเก็บไว้แบบเข้ารหัสและเปิดอ่านย้อนหลังไม่ได้ หากทำหายคุณจะต้องสร้าง endpoint ใหม่',
    'webhooks.secret.copy': 'คัดลอก secret สำหรับลงลายเซ็น',
    'webhooks.secret.verifyToggle': 'วิธีตรวจสอบลายเซ็น',
    'webhooks.secret.verify.a': 'ทุกคำขอจะมีเฮดเดอร์',
    'webhooks.secret.verify.b': 'ให้คำนวณ HMAC-SHA256 ของ',
    'webhooks.secret.verify.c': 'ด้วย secret แล้วนำไปเทียบกับ',
    'webhooks.secret.verify.d': 'แบบ constant time',
    'webhooks.secret.acknowledge': 'คัดลอกแล้ว',

    'webhooks.notFound.title': 'ไม่พบ endpoint นี้',
    'webhooks.notFound.description': 'อาจถูกลบไปแล้วหลังจากมีการแชร์ลิงก์นี้',
    'webhooks.backToList': 'กลับไปหน้า Webhooks',
    'webhooks.paused': 'หยุดชั่วคราว',

    'webhooks.metric.delivered24h': 'ส่งสำเร็จ 24 ชม.',
    'webhooks.metric.failed24h': 'ส่งไม่สำเร็จ 24 ชม.',
    'webhooks.metric.dead': 'ส่งไม่สำเร็จถาวร',
    'webhooks.metric.dead.hint': 'ลองส่งครบทุกครั้งแล้ว',
    'webhooks.metric.p95': 'ความหน่วง p95',
    'webhooks.metric.p95.hint': 'จากข้อมูลในหน้านี้',

    'webhooks.tab.deliveries': 'การส่ง',
    'webhooks.tab.tester': 'ทดสอบ',

    'webhooks.deliveryStatus.PENDING': 'อยู่ในคิว',
    'webhooks.deliveryStatus.DELIVERING': 'กำลังส่ง',
    'webhooks.deliveryStatus.SUCCEEDED': 'ส่งสำเร็จ',
    'webhooks.deliveryStatus.FAILED': 'ล้มเหลว จะลองใหม่',
    'webhooks.deliveryStatus.DEAD': 'เลิกลองแล้ว',

    'webhooks.filterStatus': 'กรองตามสถานะ',
    'webhooks.status.all': 'ทุกสถานะ',
    'webhooks.liveNotice': 'อัปเดตสดทุกครั้งที่การส่งได้ผลลัพธ์',
    'webhooks.redeliver': 'ส่งซ้ำ',
    'webhooks.deliveries.empty.title': 'ยังไม่มีการส่ง',
    'webhooks.deliveries.empty.description':
      'รายการจะปรากฏที่นี่ทันทีที่ endpoint นี้ได้รับเหตุการณ์แรก ลองส่งทดสอบเพื่อตรวจสอบการเชื่อมต่อ',

    'webhooks.delivery.title': 'การส่ง',
    'webhooks.delivery.attemptOf': 'ครั้งที่ {attempt} จาก {max} · {latency}',
    'webhooks.section.envelope': 'ซองข้อมูลของคำขอ',
    'webhooks.section.retrySchedule': 'ตารางการลองส่งซ้ำ',
    'webhooks.envelope.dataNotice':
      'endpoint รายการการส่งไม่ได้คืนค่าบล็อก data มาด้วย ฟิลด์ด้านบนคือส่วนหัวของซองข้อมูลที่ปลายทางได้รับจริง',
    'webhooks.response.none': 'ไม่ได้รับการตอบกลับ ดูข้อผิดพลาดด้านล่าง',
    'webhooks.redeliverEnvelope': 'ส่งซองข้อมูลนี้ซ้ำ',

    'webhooks.retry.deliveredOn': 'ส่งสำเร็จในครั้งที่ {attempt}',
    'webhooks.retry.stopped.one':
      'หยุดหลังจากลองส่ง {count} ครั้ง กดส่งซ้ำเพื่อลองใหม่ด้วยซองข้อมูลเดิม',
    'webhooks.retry.stopped.other':
      'หยุดหลังจากลองส่ง {count} ครั้ง กดส่งซ้ำเพื่อลองใหม่ด้วยซองข้อมูลเดิม',
    'webhooks.retry.try': 'ครั้งที่ {attempt}',
    'webhooks.retry.immediately': 'ทันที',
    'webhooks.retry.after': '+{delay} หลังจากครั้งก่อน',
    'webhooks.retry.next': 'ครั้งถัดไป',

    'webhooks.tester.eventLabel': 'เหตุการณ์ที่จะส่ง',
    'webhooks.tester.ping': 'ping (ตรวจสอบการเชื่อมต่อ)',
    'webhooks.tester.payloadLabel': 'Payload สำหรับทดสอบ',
    'webhooks.tester.invalidJson': 'JSON ไม่ถูกต้อง: {error}',
    'webhooks.tester.send': 'ส่ง',
    'webhooks.tester.idle':
      'ยังไม่ได้ส่งอะไร การตอบกลับจะแสดงที่นี่เมื่อ endpoint ตอบกลับ หรือเมื่อหมดเวลาที่ {timeout}',
    'webhooks.tester.headers': 'เฮดเดอร์ที่ส่งไป',
    'webhooks.tester.headersNote':
      'ค่าลายเซ็นถูกคำนวณบนเซิร์ฟเวอร์จาก secret ของ endpoint นี้ ตรงนี้แสดงเพียงรูปแบบเท่านั้น',
  },
});
